import CottageIcon from "@mui/icons-material/Cottage";
import { Box, Button, Dialog, Divider, Typography } from "@mui/material";
import React from "react";
import { TextView } from "../../components/TextView/TextView";
import { useHasPermission } from "../../hooks";
import { UserPermissions } from "../../utils/permissions";
import { userFriendlyDateTimestamp } from "../../utils/timestamps";
import { IPreviewCultureCategory, TabKeyEnum, TabLabelEnum } from "../../utils/types";
import { APP_Z_INDEX } from "../../utils/zIndex";
import { ViewCategoryCultures } from "./ViewCategoryCultures";

interface Props {
  activeTab: TabKeyEnum;
  category: IPreviewCultureCategory | null;
  handleClose: () => void;
  handleEditCategory: () => void;
  handlePublishCategory: () => void;
  handleUnpublishCategory: () => void;
  handleReviewCategory: () => void;
}

export const ViewCultureCategory: React.FC<Props> = props => {
  const { category, handleClose, handleEditCategory, handlePublishCategory, activeTab, handleReviewCategory, handleUnpublishCategory } =
    props;

  const canEditCategory = useHasPermission(UserPermissions.EDIT_CULTURE_CATEGORIES);
  const canApprovePublishCategory = useHasPermission(UserPermissions.APPROVE_PUBLISH_CULTURE_CATEGORIES);
  const canUnpublishCategory = useHasPermission(UserPermissions.UNPUBLISH_CULTURE_CATEGORIES);

  const showPublishButton = activeTab === TabKeyEnum.ReadyForReview && canApprovePublishCategory;
  const showEditButton = [TabKeyEnum.ReadyForReview, TabKeyEnum.Unpublished].includes(activeTab) && canEditCategory;
  const showReadyForReviewButton = activeTab === TabKeyEnum.Unpublished && canEditCategory;
  const showUnpublishButton = activeTab === TabKeyEnum.Published && canUnpublishCategory;

  const [openCategoryCultures, setOpenCategoryCultures] = React.useState<boolean>(false);

  if (!category) {
    return (
      <Typography variant="h5" component="h2">
        No category selected
      </Typography>
    );
  }

  const publishedLabel =
    category.published === "0" || category.published === TabLabelEnum.Unpublished
      ? TabLabelEnum.Unpublished
      : category.published === "-1" || category.published === TabLabelEnum.ReadyForReview
        ? TabLabelEnum.ReadyForReview
        : TabLabelEnum.Published;

  const ViewCulturesAction: React.ReactNode = (
    <Button onClick={() => setOpenCategoryCultures(true)}>
      <Box display="flex" alignItems="center" gap={0.5}>
        <CottageIcon color="primary" fontSize="small" />
        <Typography color="primary" variant="caption">
          View Cultures
        </Typography>
      </Box>
    </Button>
  );

  return (
    <Box display="flex" flexDirection="column" sx={{ width: "100%", height: "100%" }}>
      <Dialog
        open={openCategoryCultures}
        onClose={() => setOpenCategoryCultures(false)}
        fullWidth
        maxWidth="lg"
        sx={{ zIndex: APP_Z_INDEX.drawerDialog }}
      >
        <ViewCategoryCultures handleClose={() => setOpenCategoryCultures(false)} category={category} />
      </Dialog>
      <Box display="flex" flexDirection="column" gap={2} sx={{ width: "100%" }}>
        <TextView label="Name" value={category.name} />
        <TextView label="Description" value={category.description || "—"} />
        <TextView label="Published" value={publishedLabel} />
        <TextView label="Total Cultures" value={category.totalCultures ?? 0} actionComponent={ViewCulturesAction} />
        <TextView label="Created At" value={userFriendlyDateTimestamp(category.createdAt)} />
        <TextView label="Created By" value={category.createdBy || "—"} />
        <TextView label="Updated At" value={userFriendlyDateTimestamp(category.updatedAt)} />
        <TextView label="Updated By" value={category.updatedBy || "—"} />
      </Box>

      <Box sx={{ mt: "auto" }}>
        <Divider />
        <Box display="flex" justifyContent="space-between" sx={{ pt: 3, gap: 2 }}>
          {showPublishButton && (
            <Button sx={{ borderRadius: "20px" }} variant="contained" color="primary" onClick={handlePublishCategory}>
              Publish
            </Button>
          )}
          {showEditButton && (
            <Button sx={{ borderRadius: "20px" }} variant="contained" color="primary" onClick={handleEditCategory}>
              Edit
            </Button>
          )}
          {showReadyForReviewButton && (
            <Button sx={{ borderRadius: "20px" }} variant="contained" color="primary" onClick={handleReviewCategory}>
              Ready for Review
            </Button>
          )}
          {showUnpublishButton && (
            <Button sx={{ borderRadius: "20px" }} variant="outlined" color="primary" onClick={handleUnpublishCategory}>
              Unpublish
            </Button>
          )}
          <Button disableElevation variant="outlined" sx={{ borderRadius: "20px" }} onClick={handleClose}>
            <Typography sx={{ fontSize: "16px" }} color="textPrimary">
              Close
            </Typography>
          </Button>
        </Box>
      </Box>
    </Box>
  );
};
